import { useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useAccessToken } from '../../useAccessToken'
import { makeResultFetch } from '../../genericFetch'
import { apiRequestParams } from '../../fetchOptions'
import { useHandleRetry } from '../../useHandleRetry'
import {
  ProjectForecastReportPeriod,
  ProjectForecastReportRow,
} from '../../../interfaces/systemSettings/ProjectForecastReport'

const useFetch = (year: number, period: ProjectForecastReportPeriod) => {
  const getAccessToken = useAccessToken()

  const queryFunction = useCallback(async () => {
    return getAccessToken().then((accessToken) => {
      if (!accessToken) throw new Error('Access token is undefined')

      const fetchedData = makeResultFetch<ProjectForecastReportRow[]>(
        `/reports/projectForecast?year=${year}&period=${period}`,
        apiRequestParams(accessToken, 'GET')
      ).then((response) => {
        const { data, message, success } = response

        if (!success) throw Error(message)

        return data
      })

      return fetchedData
    })
  }, [getAccessToken, period, year])

  return queryFunction
}

export const useProjectForecastReports = (
  year: number,
  period: ProjectForecastReportPeriod
) => {
  const queryFunction = useFetch(year, period)
  const handleRetry = useHandleRetry()

  const { isLoading, error, data } = useQuery({
    queryKey: ['projectForecastReports', year, period],
    queryFn: queryFunction,
    enabled: !!queryFunction && !!year && !!period,
    retry: handleRetry,
  })

  return { isLoading, error, data }
}
